import React, { useCallback, useEffect, useState } from 'react';

import { useToasts } from 'react-toast-notifications';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import Modal from '@components/common/Modals/Modal/Modal';
import Pagination from '@components/common/Pagination/Pagination';
import Spinner from '@components/common/Spinner/Spinner';
import { M_DEVICE } from '@components/lpc/Constants';
import NotificationCard from '@components/lpc/Pot/MainModule/Activity/Notification/NotificationCard/NotificationCard';
import notificationService from '@services/domain/Lpc/NotificationsService';
import { getUserAvatarSrc } from '@services/utils/UserService';

import css from './ParticipantModal.module.scss';

type ParticipantModalProps = {
    isVisible: boolean;
    onHide: () => void;
    personID: number;
    potRef: string;
    personRole: string;
    setOpenParticipantModal: (value: boolean) => void;
};

const ParticipantModal = ({
    isVisible,
    onHide,
    personID,
    potRef,
    personRole,
    setOpenParticipantModal,
}: ParticipantModalProps) => {
    const { addToast } = useToasts();
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const [cursor, setCursor] = useState<number>(0);
    const [itemPerPage, setItemPerPage] = useState<number>(0);
    const [activitiesList, setActivitiesList] = useState([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { width } = useWindowSize();
    const [pageCount, setPageCount] = useState<number>(0);
    const [notificationNumber, setNotificationNumber] = useState<number>(0);
    const [pageRangeDisplayed, setPageRangeDisplayed] = useState<number>(2);

    const fetchAllNotificationsByUser = useCallback(() => {
        notificationService
            .getAllActivitiesByPersonID(potRef, personID, cursor, itemPerPage)
            .then((res) => {
                setActivitiesList(res.potActivities);
                setNotificationNumber(res.totalCount);
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => setIsLoading(false));
    }, [cursor, itemPerPage, potRef]);

    useEffect(() => {
        itemPerPage && fetchAllNotificationsByUser();
    }, [itemPerPage, cursor]);

    useEffect(() => {
        setCursor(0);
    }, [itemPerPage]);

    useEffect(() => {
        if (notificationNumber > 0) {
            setPageCount(Math.ceil(notificationNumber / itemPerPage));
        }
    }, [notificationNumber, itemPerPage]);

    useEffect(() => {
        width && (width < M_DEVICE ? setItemPerPage(3) : setItemPerPage(5));
    }, [width]);

    const handlePageClick = (page) => {
        setPageRangeDisplayed(2);
        const pageSelected = page.selected;
        const offset = Math.ceil(pageSelected * itemPerPage);
        setCursor(offset);
    };

    const onUpdateActivities = () => {
        setCursor(0);
        fetchAllNotificationsByUser();
    };

    return (
        <Modal visible={isVisible} onHide={onHide}>
            <div className={css.participant_modal} id={'notifContainer'}>
                {isLoading ? (
                    <>
                        <Spinner />
                    </>
                ) : (
                    <>
                        {activitiesList.map((item) => (
                            <NotificationCard
                                orderId={item.participant.orderId}
                                message={item.participant.message}
                                hideName={item.participant.hideName}
                                hideContribution={item.participant.hideContribution}
                                contribution={item.participant.contribution}
                                onUpdateActivities={onUpdateActivities}
                                key={item.uniq}
                                id={item.uniq}
                                avatar={getUserAvatarSrc(item.participant)}
                                firstName={item.participant.firstName}
                                lastName={item.participant.lastName}
                                creationDate={item.date}
                                type={item.type}
                                isOrganiser={item.isOrganiser}
                                personRole={personRole}
                                potRef={potRef}
                                setOpenParticipantModal={setOpenParticipantModal}
                            />
                        ))}
                        <div className={css.participant_modal__pagination}>
                            <Pagination
                                handlePageClick={handlePageClick}
                                pageCount={pageCount}
                                pageRangeDisplayed={pageRangeDisplayed}
                                marginPagesDisplayed={1}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ParticipantModal;
